import { z } from 'zod'

/**
 * Catálogo de produtos e Revisão de Estoque (arquitetura §7) — F3.
 *
 * Duas populações com perguntas diferentes: produto de SAÍDA (como tributo em
 * 2027?) e insumo de ENTRADA (isso me dá crédito?). O mesmo item físico pode
 * estar nas duas — registros ligados, nunca fundidos.
 */

export const TIPOS_PRODUTO = ['SAIDA', 'ENTRADA'] as const
export type TipoProduto = (typeof TIPOS_PRODUTO)[number]

export const TRATAMENTOS = [
  'INTEGRAL',
  'REDUCAO_60',
  'REDUCAO_50',
  'REDUCAO_30',
  'ALIQUOTA_ZERO',
  'ISENCAO',
  'IMUNIDADE',
  'DIFERIMENTO',
  'MONOFASICO',
  'REDUCAO_70',
  'REGIME_ESPECIFICO',
] as const
export type Tratamento = (typeof TRATAMENTOS)[number]

export const ROTULO_TRATAMENTO: Record<Tratamento, string> = {
  INTEGRAL: 'Tributação integral',
  REDUCAO_60: 'Redução de 60%',
  REDUCAO_50: 'Redução de 50% (venda de imóvel)',
  REDUCAO_30: 'Redução de 30%',
  REDUCAO_70: 'Redução de 70% (locação de imóvel)',
  ALIQUOTA_ZERO: 'Alíquota zero',
  ISENCAO: 'Isenção',
  IMUNIDADE: 'Imunidade',
  DIFERIMENTO: 'Diferimento',
  MONOFASICO: 'Monofásico',
  REGIME_ESPECIFICO: 'Regime específico',
}

export const DESTINACOES = ['REVENDA', 'INSUMO', 'USO_CONSUMO', 'ATIVO_IMOBILIZADO', 'NAO_APLICAVEL'] as const
export type Destinacao = (typeof DESTINACOES)[number]

export const ROTULO_DESTINACAO: Record<Destinacao, string> = {
  REVENDA: 'Revenda',
  INSUMO: 'Insumo',
  USO_CONSUMO: 'Uso e consumo',
  ATIVO_IMOBILIZADO: 'Ativo imobilizado',
  NAO_APLICAVEL: 'Não se aplica',
}

/** De onde veio o perfil. LLM nunca é aprovado sozinho (§7.4, regra de ouro). */
export const ORIGENS_PERFIL = ['DETERMINISTICO', 'HEURISTICO', 'LLM', 'MANUAL', 'HERDADO'] as const
export type OrigemPerfil = (typeof ORIGENS_PERFIL)[number]

/**
 * PENDENTE: sem sugestão · SUGERIDO: há sugestão aguardando revisão ·
 * APROVADO: perfil vigente assinado por alguém · REVISAR: aprovado, mas o dado
 * de origem mudou (NCM novo nas notas, versão da base) — pede novo olhar.
 */
export const STATUS_REVISAO = ['PENDENTE', 'SUGERIDO', 'APROVADO', 'REVISAR'] as const
export type StatusRevisao = (typeof STATUS_REVISAO)[number]

export const Ncm = z.string().regex(/^\d{8}$/, 'NCM tem 8 dígitos')
/** cClassTrib oficial: 6 dígitos, os 3 primeiros são o CST do IBS/CBS. */
export const CClassTrib = z.string().regex(/^\d{6}$/, 'cClassTrib tem 6 dígitos')
export const CstIbsCbs = z.string().regex(/^\d{3}$/, 'CST do IBS/CBS tem 3 dígitos')

export const PerfilTributario = z
  .object({
    ncm: Ncm,
    ncmEx: z.string().max(3).nullable(),
    cest: z.string().regex(/^\d{7}$/).nullable(),
    cClassTrib: CClassTrib,
    cstIbsCbs: CstIbsCbs,
    tratamento: z.enum(TRATAMENTOS),
    cestaBasicaNacional: z.boolean(),
    impostoSeletivo: z.object({ incide: z.boolean(), fundamento: z.string().max(300).nullable() }),
    destinacao: z.enum(DESTINACOES),
    daCredito: z.boolean(),
    fundamento: z.string().min(3, 'Informe o fundamento legal').max(1000),
  })
  .refine((p) => p.cClassTrib.startsWith(p.cstIbsCbs), {
    message: 'O cClassTrib começa pelo CST: os 3 primeiros dígitos precisam ser iguais',
    path: ['cClassTrib'],
  })
export type PerfilTributario = z.infer<typeof PerfilTributario>

/** Sugestão das camadas 1–2: o perfil proposto, com confiança e as regras que o produziram. */
export const SugestaoPerfil = z.object({
  camada: z.enum(['DETERMINISTICO', 'HEURISTICO']),
  perfil: PerfilTributario,
  confianca: z.number().min(0).max(1),
  regras: z.array(z.string()),
  alertas: z.array(z.string()),
  versaoKb: z.string(),
})
export type SugestaoPerfil = z.infer<typeof SugestaoPerfil>

export const FILTROS_PRODUTO = ['sem_ncm', 'ncm_invalido', 'confianca_baixa', 'com_is', 'revisar', 'pendente'] as const

export const FiltroProduto = z.object({
  tipo: z.enum(TIPOS_PRODUTO),
  status: z.enum(STATUS_REVISAO).optional(),
  alerta: z.enum(FILTROS_PRODUTO).optional(),
  ncm: z.string().regex(/^\d{2,8}$/).optional(),
  busca: z.string().trim().max(100).optional(),
  pagina: z.number().int().min(1).default(1),
  porPagina: z.number().int().min(10).max(200).default(50),
})
export type FiltroProduto = z.infer<typeof FiltroProduto>
